import React, {
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiChevronDown,
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiX,
} from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/header.scss";

const DRAWER_FOCUSABLE_SELECTOR = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
].join(",");

const getPageMeta = (pathname, search) => {
  const normalizedPath =
    pathname !== "/" ? pathname.replace(/\/+$/, "") : pathname;
  const detailMatch = normalizedPath.match(/^\/shopDetails\/([^/]+)$/);

  if (detailMatch) {
    const productId = detailMatch[1];
    const product = productData.find(
      (item) => String(item.id) === String(productId)
    );

    return product
      ? {
          title: `${product.name} | ${siteConfig.brandName}`,
          description: product.description,
        }
      : {
          title: `Product unavailable | ${siteConfig.brandName}`,
          description: `That item is no longer available. Explore the full ${siteConfig.brandName} stationery collection.`,
        };
  }

  if (normalizedPath === "/shop") {
    const params = new URLSearchParams(search);
    const query = params.get("q")?.trim();
    const category = params.get("category")?.trim();
    const subcategory = params.get("subcategory")?.trim();
    const maker = params.get("maker")?.trim();
    let pageLabel = "Shop stationery";

    if (params.get("saved") === "1") pageLabel = "Saved stationery";
    else if (params.get("new") === "1") pageLabel = "New arrivals";
    else if (maker) pageLabel = `${maker} stationery`;
    else if (subcategory) pageLabel = subcategory;
    else if (category) pageLabel = category;
    else if (query) pageLabel = `Search: ${query.slice(0, 60)}`;

    return {
      title: `${pageLabel} | ${siteConfig.brandName}`,
      description: `Browse considered pens, pencils, paper, art supplies, and practical desk tools from ${siteConfig.brandName}.`,
    };
  }

  const routeMeta = {
    "/": {
      title: `${siteConfig.brandName} | Thoughtful stationery for everyday ideas`,
      description: siteConfig.description,
    },
    "/about": {
      title: `About ${siteConfig.brandName} | Tools for thoughtful work`,
      description: `Learn how ${siteConfig.brandName} selects useful, lasting stationery for writing, drawing, planning, and everyday creative work.`,
    },
    "/shipping": {
      title: `Shipping information | ${siteConfig.brandName}`,
      description: `Review ${siteConfig.brandName} shipping from ${siteConfig.businessLocation.countryShortName}, including processing times, delivery methods, costs, tracking, and destinations.`,
    },
    "/returns": {
      title: `Returns | ${siteConfig.brandName}`,
      description: `Read the ${siteConfig.brandName} return policy, eligibility guidance, return process, and refund information.`,
    },
    "/contact": {
      title: `Contact us | ${siteConfig.brandName}`,
      description: `Contact the ${siteConfig.businessLocation.countryShortName}-based ${siteConfig.brandName} customer-care team about products, orders, shipping, or returns.`,
    },
    "/terms": {
      title: `Terms of use | ${siteConfig.brandName}`,
      description: `Read the terms for using the ${siteConfig.brandName} storefront and shopping experience.`,
    },
    "/privacy": {
      title: `Privacy policy | ${siteConfig.brandName}`,
      description: `Learn how ${siteConfig.brandName} handles cart, wishlist, checkout, and contact information.`,
    },
    "/cookies": {
      title: `Cookie policy | ${siteConfig.brandName}`,
      description: `Learn about essential browser storage and cookie choices at ${siteConfig.brandName}.`,
    },
    "/faqs": {
      title: `Frequently asked questions | ${siteConfig.brandName}`,
      description: `Find answers about ${siteConfig.brandName} orders, delivery, returns, products, and payment.`,
    },
    "/cartpage": {
      title: `Your cart | ${siteConfig.brandName}`,
      description: `Review quantities, saved stationery, shipping, and the order total in your ${siteConfig.brandName} cart.`,
    },
    "/checkout": {
      title: `Checkout | ${siteConfig.brandName}`,
      description: `Complete the secure checkout for your ${siteConfig.brandName} stationery order.`,
    },
    "/paysuccess": {
      title: `Order confirmed | ${siteConfig.brandName}`,
      description: `Review the payment status for your ${siteConfig.brandName} stationery order.`,
    },
    "/payment": {
      title: `Payment status | ${siteConfig.brandName}`,
      description: `Review the payment status for your ${siteConfig.brandName} order.`,
    },
  };

  return (
    routeMeta[normalizedPath] || {
      title: `${siteConfig.brandName} | ${siteConfig.brandTagline}`,
      description: siteConfig.description,
    }
  );
};

const getNavigationChild = (child) => {
  if (typeof child === "string") {
    return {
      label: child,
      to: `/shop?subcategory=${encodeURIComponent(child)}`,
    };
  }
  return child;
};

const Header = () => {
  const { cartCount, wishlistCount } = useContext(CartContext);
  const navigate = useNavigate();
  const location = useLocation();
  const instanceId = useId().replace(/:/g, "");
  const desktopSearchId = `header-search-${instanceId}`;
  const searchResultsId = `header-search-results-${instanceId}`;
  const searchRef = useRef(null);
  const menuButtonRef = useRef(null);
  const drawerRef = useRef(null);
  const drawerCloseRef = useRef(null);
  const restoreMenuFocusRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState("");
  const [accountNotice, setAccountNotice] = useState(false);

  const searchSuggestions = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (query.length < 2) return [];
    return productData
      .filter((product) =>
        [product.name, product.brand, product.category, product.subcategory]
          .join(" ")
          .toLowerCase()
          .includes(query)
      )
      .slice(0, 5);
  }, [searchTerm]);

  useEffect(() => {
    if (restoreMenuFocusRef.current) {
      restoreMenuFocusRef.current = false;
      menuButtonRef.current?.focus({ preventScroll: true });
    }
    setMenuOpen(false);
    setOpenMenu("");
    setSearchOpen(false);
    setAccountNotice(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const pageMeta = getPageMeta(location.pathname, location.search);
    document.title = pageMeta.title;

    const descriptionMeta = document.querySelector('meta[name="description"]');
    if (descriptionMeta) {
      descriptionMeta.setAttribute("content", pageMeta.description);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      !window.navigator.userAgent.toLowerCase().includes("jsdom")
    ) {
      if (location.hash) {
        const focusFrame = window.requestAnimationFrame(() => {
          document
            .getElementById(location.hash.slice(1))
            ?.scrollIntoView({ block: "start" });
        });
        return () => window.cancelAnimationFrame(focusFrame);
      }
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
    return undefined;
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    restoreMenuFocusRef.current = true;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => {
      drawerCloseRef.current?.focus();
    });

    const handleDrawerKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        restoreMenuFocusRef.current = false;
        menuButtonRef.current?.focus({ preventScroll: true });
        setMenuOpen(false);
        setOpenMenu("");
        return;
      }

      if (event.key !== "Tab" || !drawerRef.current) return;
      const focusableElements = Array.from(
        drawerRef.current.querySelectorAll(DRAWER_FOCUSABLE_SELECTOR)
      );

      if (!focusableElements.length) {
        event.preventDefault();
        drawerRef.current.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const focusIsOutside = !drawerRef.current.contains(
        document.activeElement
      );

      if (
        event.shiftKey &&
        (document.activeElement === firstElement || focusIsOutside)
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === lastElement || focusIsOutside)
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleDrawerKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleDrawerKeyDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  const closeMobileMenu = () => {
    restoreMenuFocusRef.current = false;
    menuButtonRef.current?.focus({ preventScroll: true });
    setMenuOpen(false);
    setOpenMenu("");
  };

  const submitSearch = (event) => {
    event.preventDefault();
    const query = searchTerm.trim();
    if (menuOpen) closeMobileMenu();
    navigate(query ? `/shop?q=${encodeURIComponent(query)}` : "/shop");
    setSearchOpen(false);
  };

  const toggleDesktopMenu = (label) => {
    setOpenMenu((current) => (current === label ? "" : label));
  };

  const drawerTabIndex = menuOpen ? undefined : -1;

  return (
    <>
      <header className="site-header">
        <div className="site-header__announcement">
          {siteConfig.announcement}
        </div>

        <div className="site-header__mobile-row">
          <button
            ref={menuButtonRef}
            type="button"
            className="site-header__icon-button"
            onClick={() => setMenuOpen(true)}
            aria-label="Open navigation"
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation-drawer"
          >
            <FiMenu aria-hidden="true" />
          </button>
          <Link
            className="site-header__mobile-logo"
            to="/"
            aria-label={siteConfig.logo.ariaLabel}
          >
            {siteConfig.logo.text}
          </Link>
          <Link
            className="site-header__mobile-cart"
            to="/cartpage"
            aria-label={`Cart with ${cartCount} items`}
          >
            <span>{cartCount}</span>
            <FiShoppingBag aria-hidden="true" />
          </Link>
        </div>

        <div className="site-header__desktop-row ink-container">
          <div className="site-header__search-wrap" ref={searchRef}>
            <form
              className={`site-header__search ${searchOpen ? "is-open" : ""}`}
              onSubmit={submitSearch}
              role="search"
            >
              <FiSearch aria-hidden="true" />
              <label htmlFor={desktopSearchId}>Search</label>
              <input
                id={desktopSearchId}
                type="search"
                value={searchTerm}
                autoComplete="off"
                onFocus={() => setSearchOpen(true)}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setSearchOpen(true);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Escape") setSearchOpen(false);
                }}
                aria-controls={searchResultsId}
              />
              <button type="submit" aria-label="Submit search">
                <span aria-hidden="true">→</span>
              </button>
            </form>

            {searchOpen && searchTerm.trim().length >= 2 && (
              <div
                className="site-header__search-results"
                id={searchResultsId}
                aria-live="polite"
              >
                {searchSuggestions.length ? (
                  <>
                    {searchSuggestions.map((product) => (
                      <Link
                        to={`/shopDetails/${product.id}`}
                        key={product.id}
                        className="site-header__search-result"
                      >
                        <img src={product.image} alt="" />
                        <span>
                          <strong>{product.name}</strong>
                          <small>{product.brand}</small>
                        </span>
                      </Link>
                    ))}
                    <Link
                      className="site-header__search-all"
                      to={`/shop?q=${encodeURIComponent(searchTerm.trim())}`}
                    >
                      See all results
                    </Link>
                  </>
                ) : (
                  <p>No stationery found. Try another word.</p>
                )}
              </div>
            )}
          </div>

          <Link
            className="site-header__logo"
            to="/"
            aria-label={siteConfig.logo.ariaLabel}
          >
            <span>{siteConfig.logo.text}</span>
            <small>{siteConfig.brandTagline}</small>
          </Link>

          <div className="site-header__utilities">
            <div className="site-header__account-wrap">
              {accountNotice && (
                <div className="site-header__account-note" role="status">
                  Customer accounts are not currently available.
                </div>
              )}
            </div>
            <Link className="site-header__saved" to="/shop?saved=1">
              <FiHeart aria-hidden="true" />
              <span>Saved ({wishlistCount})</span>
            </Link>
            <Link className="site-header__cart-button" to="/cartpage">
              Cart ({cartCount})
            </Link>
          </div>
        </div>

        <nav className="site-header__nav" aria-label="Primary navigation">
          <ul>
            {siteConfig.navigation.map((item) => (
              <li
                key={item.label}
                className={openMenu === item.label ? "is-open" : ""}
              >
                <button
                  type="button"
                  onClick={() => toggleDesktopMenu(item.label)}
                  aria-expanded={openMenu === item.label}
                >
                  {item.label}
                  <FiChevronDown aria-hidden="true" />
                </button>
                <div className="site-header__submenu">
                  <Link
                    to={`/shop?category=${encodeURIComponent(item.category)}`}
                  >
                    All {item.label.toLowerCase()}
                  </Link>
                  {item.children.map((child) => {
                    const navigationChild = getNavigationChild(child);
                    return (
                      <Link key={navigationChild.label} to={navigationChild.to}>
                        {navigationChild.label}
                      </Link>
                    );
                  })}
                </div>
              </li>
            ))}
            <li>
              <Link className="site-header__nav-link" to="/shop">
                SHOP ALL
              </Link>
            </li>
            <li>
              <Link className="site-header__nav-link" to="/shop?q=kids">
                KIDS STATIONERY
              </Link>
            </li>
          </ul>
        </nav>
      </header>

      <button
        type="button"
        className={`site-header__scrim ${menuOpen ? "is-visible" : ""}`}
        onClick={closeMobileMenu}
        aria-label="Close navigation"
        tabIndex={menuOpen ? 0 : -1}
      />
      <aside
        ref={drawerRef}
        id="mobile-navigation-drawer"
        className={`site-header__drawer ${menuOpen ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
        aria-hidden={!menuOpen}
        tabIndex={-1}
      >
        <form className="site-header__drawer-search" onSubmit={submitSearch}>
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search"
            aria-label="Search products"
            tabIndex={drawerTabIndex}
          />
          <button
            type="submit"
            aria-label="Submit search"
            tabIndex={drawerTabIndex}
          >
            <FiSearch aria-hidden="true" />
          </button>
        </form>
        <button
          ref={drawerCloseRef}
          type="button"
          className="site-header__drawer-close"
          onClick={closeMobileMenu}
          aria-label="Close navigation"
          tabIndex={drawerTabIndex}
        >
          <FiX aria-hidden="true" />
        </button>
        <nav aria-label="Mobile navigation">
          <ul>
            {siteConfig.navigation.map((item) => (
              <li key={item.label}>
                <button
                  type="button"
                  onClick={() => toggleDesktopMenu(item.label)}
                  aria-expanded={openMenu === item.label}
                  aria-controls={`mobile-submenu-${item.label
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  tabIndex={drawerTabIndex}
                >
                  {item.label}
                  <FiChevronDown aria-hidden="true" />
                </button>
                <div
                  id={`mobile-submenu-${item.label
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  className={openMenu === item.label ? "is-open" : ""}
                  aria-hidden={openMenu !== item.label}
                >
                  <Link
                    to={`/shop?category=${encodeURIComponent(item.category)}`}
                    onClick={closeMobileMenu}
                    tabIndex={
                      menuOpen && openMenu === item.label ? undefined : -1
                    }
                  >
                    All {item.label.toLowerCase()}
                  </Link>
                  {item.children.map((child) => {
                    const navigationChild = getNavigationChild(child);
                    return (
                      <Link
                        key={navigationChild.label}
                        to={navigationChild.to}
                        onClick={closeMobileMenu}
                        tabIndex={
                          menuOpen && openMenu === item.label ? undefined : -1
                        }
                      >
                        {navigationChild.label}
                      </Link>
                    );
                  })}
                </div>
              </li>
            ))}
            <li>
              <Link
                to="/shop?new=1"
                onClick={closeMobileMenu}
                tabIndex={drawerTabIndex}
              >
                NEW ARRIVALS
              </Link>
            </li>
            <li>
              <Link
                to="/shop?q=kids"
                onClick={closeMobileMenu}
                tabIndex={drawerTabIndex}
              >
                KIDS STATIONERY
              </Link>
            </li>
            <li>
              <Link
                to="/shop?saved=1"
                onClick={closeMobileMenu}
                tabIndex={drawerTabIndex}
              >
                SAVED ({wishlistCount})
              </Link>
            </li>
            <li>
              <Link
                to="/about"
                onClick={closeMobileMenu}
                tabIndex={drawerTabIndex}
              >
                OUR STORY
              </Link>
            </li>
          </ul>
        </nav>
      </aside>
    </>
  );
};

export default Header;
